import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/** The line-height to fall back on when the cell has not been styled with one yet. */
const FALLBACK_LINE_HEIGHT = 19;

/**
 * A table cell's content, held to two lines.
 *
 * A long value — a full URL, a headline, a list of keywords — otherwise sets the height of its
 * whole row, so one such column turns a table into pages of scrolling. Two lines is enough to
 * recognise a value by, and the rest is reachable as the cell's hover title. The title is written
 * only once the content is actually cut, and only when the pointer arrives, so a value that fits
 * never grows a redundant tooltip and a table of hundreds of cells measures nothing until asked.
 *
 * Content that is not reflowable text keeps its own height instead: a sparkline or a thumbnail is
 * what the cell is for, and half of one says nothing. Such a box is recognised by standing taller
 * than the two lines while carrying no text of its own, which leaves icons and favicons — both
 * well under that height — clamped along with the text beside them.
 */
export default function CellContent({ children }) {
    const ref = useRef(null);
    const [clamped, setClamped] = useState(true);
    const [title, setTitle] = useState(undefined);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;

        const lineHeight =
            parseFloat(window.getComputedStyle(el).lineHeight) || FALLBACK_LINE_HEIGHT;
        const limit = lineHeight * 2;

        const hasOwnHeight = Array.from(el.querySelectorAll('*')).some(
            (node) => !node.textContent.trim() && node.offsetHeight > limit,
        );

        setClamped(!hasOwnHeight);
    }, [children]);

    const handleEnter = useCallback(() => {
        const el = ref.current;
        if (!el) return;

        const cut = el.scrollHeight > el.clientHeight + 1;
        setTitle(cut ? (el.innerText || el.textContent || '').trim() : undefined);
    }, []);

    return (
        <span
            ref={ref}
            className={`dt-cell-content ${clamped ? '' : 'is-open'}`}
            title={title}
            onMouseEnter={handleEnter}
        >
            {children}
        </span>
    );
}
